import React, { useRef, useState, useEffect } from 'react';
import Hls from 'hls.js';
import { startLiveCatchupMonitor, schedulePlayLiveSync, HLS_LIVE_CONFIG, MOBILE_CATCHUP_OPTIONS } from '../lib/livePlaybackCatchup';
import { buildHlsPlayerStreamUrl } from '../lib/streamingUrl';
import { attachSessionPlayback, isSessionActive } from '../lib/sessionActivity';

// Stessa gestione live di VideoPlayerHls (PC): solo adattamento URL/props mobile.
const VideoPlayerResponsiveHls = ({
  videoStreaming,
  nomeDispositivo,
  videoStreamingName,
  isDrone,
  streamingSenzaSeqMission = [],
}) => {
  const videoRef = useRef(null);
  const [videoSrc, setVideoSrc] = useState('');
  const [reloadTrigger, setReloadTrigger] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const deviceType = isDrone ? 'Drone' : 'ElicotteroH264';
    const newVideoSrc = buildHlsPlayerStreamUrl({
      baseUrl: videoStreaming,
      seriale: nomeDispositivo,
      missionNum: videoStreamingName,
      deviceType,
      streamingSenzaSeqMission,
    });
    setVideoSrc(newVideoSrc || '');
  }, [videoStreaming, nomeDispositivo, videoStreamingName, isDrone, streamingSenzaSeqMission]);

  useEffect(() => {
    let hls = null;
    let reloadTimeoutId = null;
    let loadedMetadataHandler = null;
    let stopLiveCatchup = null;

    setIsLoading(true);

    const markFrameReady = () => {
      if (videoRef.current) {
        try {
          videoRef.current.muted = false;
        } catch (_) {
          /* ignore */
        }
      }
      setIsLoading(false);
    };

    const playVideo = () => {
      if (!isSessionActive() || !videoRef.current) {
        return;
      }

      const playPromise = videoRef.current.play();
      if (playPromise === undefined) {
        return;
      }

      playPromise.catch(() => {});
    };

    const handlePlay = () => {
      schedulePlayLiveSync(videoRef.current, () => hls, MOBILE_CATCHUP_OPTIONS);
    };

    const handleVisibilityChange = () => {
      if (!isSessionActive() || document.hidden || !videoRef.current) {
        return;
      }
      if (videoRef.current.paused) {
        playVideo();
      }
      schedulePlayLiveSync(videoRef.current, () => hls, MOBILE_CATCHUP_OPTIONS);
    };

    const initializeHls = () => {
      if (!videoSrc) {
        return;
      }

      if (Hls.isSupported()) {
        hls = new Hls({
          ...HLS_LIVE_CONFIG,
        });
        hls.loadSource(videoSrc);
        hls.attachMedia(videoRef.current);

        hls.on(Hls.Events.ERROR, function (event, data) {
          if (data.fatal) {
            console.error('Errore fatale durante il caricamento del video:', data.type, data.details);

            if (!isSessionActive()) {
              return;
            }

            if (data.type !== Hls.ErrorTypes.NETWORK_ERROR || data.details !== Hls.ErrorDetails.MANIFEST_PARSING_ERROR) {
              if (reloadTimeoutId) {
                clearTimeout(reloadTimeoutId);
              }
              reloadTimeoutId = setTimeout(() => {
                if (!isSessionActive()) {
                  return;
                }
                setReloadTrigger((prevState) => !prevState);
              }, 5000);
            }
          }
        });

        hls.on(Hls.Events.MANIFEST_PARSED, function () {
          if (videoRef.current) {
            loadedMetadataHandler = () => {
              playVideo();
            };
            videoRef.current.addEventListener('loadedmetadata', loadedMetadataHandler);
          }
        });
      } else if (videoRef.current && videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
        // Fallback necessario su Safari iOS quando MSE/hls.js non è disponibile.
        videoRef.current.src = videoSrc;
        loadedMetadataHandler = () => {
          playVideo();
        };
        videoRef.current.addEventListener('loadedmetadata', loadedMetadataHandler);
      } else {
        console.error('Questo browser non supporta HLS');
      }
    };

    initializeHls();

    const detachSessionPlayback = attachSessionPlayback(videoRef.current, () => hls);
    const videoElement = videoRef.current;
    if (videoElement) {
      videoElement.addEventListener('play', handlePlay);
      videoElement.addEventListener('playing', markFrameReady);
      videoElement.addEventListener('loadeddata', markFrameReady);
      stopLiveCatchup = startLiveCatchupMonitor(videoElement, () => hls, MOBILE_CATCHUP_OPTIONS);
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      detachSessionPlayback();

      if (stopLiveCatchup) {
        stopLiveCatchup();
        stopLiveCatchup = null;
      }

      document.removeEventListener('visibilitychange', handleVisibilityChange);

      if (videoElement) {
        videoElement.removeEventListener('play', handlePlay);
        videoElement.removeEventListener('playing', markFrameReady);
        videoElement.removeEventListener('loadeddata', markFrameReady);
        videoElement.pause();
        videoElement.removeAttribute('src');
        videoElement.load();
      }

      if (videoRef.current && loadedMetadataHandler) {
        videoRef.current.removeEventListener('loadedmetadata', loadedMetadataHandler);
        loadedMetadataHandler = null;
      }

      if (hls) {
        hls.destroy();
      }
      if (reloadTimeoutId) {
        clearTimeout(reloadTimeoutId);
      }
    };
  }, [videoSrc, reloadTrigger]);

  return (
    <div className="live-stream-mobile-shell">
      <video
        autoPlay
        playsInline
        muted
        controls={!isLoading}
        ref={videoRef}
        className="custom-video-player-cell"
        key={reloadTrigger}
      ></video>
      {isLoading && (
        <div className="live-stream-mobile-loading" aria-busy="true" aria-label="Caricamento streaming">
          <div className="live-stream-mobile-spinner" />
        </div>
      )}
    </div>
  );
};

export default VideoPlayerResponsiveHls;
